import type { TableSnapshot } from "./tableMemory";

export interface ArrivalClaimInput {
  /** The id in `?view=`, if the URL named one. */
  linkedId: string | undefined;
  /** What this tab remembers for the table, or `null` for nothing. */
  snapshot: TableSnapshot | null;
}

/**
 * Whether a resource table's arrival was claimed before it mounted.
 *
 * The arrival order (ENG-3021): a `?view=` link, then what this tab was doing
 * (ENG-3035), then the customer's default View, then their org's default View
 * (ENG-2890), then everything unfiltered. The first two are known before the
 * first render — the link is in the URL and the snapshot is in storage — so
 * they are decided here, once, and the defaults (`useDefaultViewArrival`, the
 * only source that fetches) are told to stand down. Letting every source
 * start and racing them would hand the arrival to whichever fetch finished
 * last.
 *
 * A boolean rather than which source won: no consumer needs to know. The
 * default View stands down for either, and `useRestoredTableState` already
 * knows whether it seeded from memory.
 *
 * A snapshot claims the arrival even when it is empty. Clearing back to
 * unfiltered is a decision the customer made, and one the default View must not
 * undo on the next visit; treating "nothing filtered" as "nothing remembered"
 * is precisely how that decision would get lost.
 */
export function isArrivalClaimed({
  linkedId,
  snapshot,
}: ArrivalClaimInput): boolean {
  return linkedId !== undefined || snapshot !== null;
}
